import { AsyncPipe, JsonPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import moment from 'moment';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { GradeLesson } from '../../../../models/grade-lesson.school.model';
import { Grade } from '../../../../models/grade.school.model';
import { LessonStatus } from '../../../../models/lesson.school.model';
import { UserModel } from '../../../../models/user.model';
import { SchoolGradeLessonService } from '../../../../services/grade-lesson.school.service';
import * as gradeLessonActions from '../../../../state/actions/grade-lesson.school.actions';
import { AppState } from '../../../../state/appstate';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';

@Component({
  selector: 'app-dlg-upsert-grade-lesson',
  standalone: true,
  imports: [BtnSubmitComponent, AsyncPipe, ReactiveFormsModule, JsonPipe],
  templateUrl: './dlg-upsert-grade-lesson.component.html',
  styleUrl: './dlg-upsert-grade-lesson.component.css',
})
export class DlgUpsertGradeLessonComponent {
  store: Store<AppState> = inject(Store<AppState>);
  private actions$: Actions = inject(Actions);
  gradeLessonService: SchoolGradeLessonService = inject(
    SchoolGradeLessonService,
  );
  readonly dialogRef = inject(MatDialogRef<DlgUpsertGradeLessonComponent>);
  private dialog: MatDialog = inject(MatDialog);

  readonly data = inject<any>(MAT_DIALOG_DATA);
  fb = inject(FormBuilder);

  user: UserModel | null = null;
  grade: Grade | null = null;

  pageForm: FormGroup;

  tags: string[] = [];

  title: string = 'Create Grade Lesson';
  description: string = 'Enter Grade Lesson Details';
  action: string = 'Create';

  lessonStatus = LessonStatus;

  minTime = dayjs().format('YYYY-MM-DDTHH:mm');

  loading$: Observable<boolean> = of(false);

  exit$: Subject<void> = new Subject<void>();
  constructor() {
    this.pageForm = this.fb.group({
      lessonName: new FormControl(this.data.lesson?.lessonName, [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
      ]),
      publishBy: new FormControl(
        this.data.lesson?.publishBy
          ? dayjs(
              this.data.lesson.publishBy.toDate
                ? this.data.lesson.publishBy.toDate()
                : this.data.lesson.publishBy,
            ).format('YYYY-MM-DDTHH:mm')
          : '',
      ),
    });

    this.title = this.data.title;
    this.description = this.data.description;
    this.action = this.data.action;
  }

  ngOnInit(): void {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    // Subscribe to loadUserSuccess action
    this.actions$
      .pipe(ofType(gradeLessonActions.updateSuccess), takeUntil(this.exit$))
      .subscribe(() => {
        this.loading$ = of(false);
        this.dialogRef.close(true);
      });

    // Subscribe to updateFailure action (optional, for error handling)
    this.actions$
      .pipe(ofType(gradeLessonActions.updateFailure), takeUntil(this.exit$))
      .subscribe(({ error }) => {
        this.loading$ = of(false);
        this.onShowAlert(error);
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  async submit(): Promise<void> {
    if (this.pageForm.invalid) {
      const exp = {
        title: 'Invalid Form',
        message: 'Please fill out all required fields',
      };
      this.onShowAlert(exp);
      return;
    }
    this.loading$ = of(true);
    try {
      //
      // Create GradeLesson does not exist. You can only add from Lesson repository
      //
      // if (this.data.lesson?.id == undefined) {
      //   const gradeLesson: GradeLesson = {
      //     lessonName: this.pageForm.get('lessonName')?.value,
      //     lessonStatus: GradeLessonStatus.open,
      //     publishBy: this.pageForm.get('publishBy')?.value,
      //     groupId: this.data.lesson.groupId,
      //     gradeId: this.data.lesson.gradeId,
      //     academicYear: this.data.lesson.academicYear,
      //     avgAge: this.data.grade.gradeAvgAge,
      //     tags: this.tags,
      //     createdBy: this.user?.name,
      //     createdById: this.user?.uid,
      //     memberPhotoUrl: this.user?.photoURL,
      //     created: Timestamp.now(),
      //     modified: Timestamp.now(),
      //   } as GradeLesson;
      //   // console.log(
      //   //   `DlgUpsertGradeLessonComponent :: create : gradeLesson: ${JSON.stringify(
      //   //     gradeLesson,
      //   //     null,
      //   //     2
      //   //   )}`
      //   // );
      //   const lessonId: string =
      //     await this.gradeLessonService.createGradeLesson(
      //       this.data.grade.groupId,
      //       gradeLesson
      //     );
      //   // this.dialogRef.close({ ...gradeLesson, id: lessonId });
      //   this.dialogRef.close(true);

      //   // this.store.dispatch(CreateGradeLesson)

      // } else {
      const gradeLesson: GradeLesson = {
        ...this.data.lesson,
        lessonName: this.pageForm.get('lessonName')?.value,
        publishBy: Timestamp.fromDate(
          moment(this.pageForm.get('publishBy')?.value).toDate(),
        ),
        groupId: this.data.lesson.groupId,
        gradeId: this.data.lesson.gradeId,
        academicYear: this.data.lesson.academicYear,
        modified: Timestamp.now(),
      } as GradeLesson;

      this.store.dispatch(
        gradeLessonActions.update({
          groupId: this.data.lesson.groupId,
          lessonId: this.data.lesson.id,
          gradeLesson: gradeLesson,
        }),
      );
      // }
    } catch (error) {
      const exp: AppExceptionModel = {
        code: 'ERROR_CREATE_GRADELESSON',
        title: 'Error Occurred!',
        message: `Error creating/updating the grade lesson.\nAdditional info: ${error}`,
        error: error,
      } as AppExceptionModel;
      this.onShowAlert(exp);
    }
    this.loading$ = of(false);
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title,
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
